import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import dayjs from 'dayjs';
import { adminApi, callbackRequestsApi, eventsApi } from '../../services/api';
import { AppUser, CallbackRequestItem, Event } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_OPTIONS = ['new', 'in_progress', 'resolved', 'cancelled'] as const;

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-50 text-blue-700 border border-blue-200',
  in_progress: 'bg-yellow-50 text-yellow-700 border border-yellow-200',
  resolved: 'bg-green-50 text-green-700 border border-green-200',
  cancelled: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const CallbackRequests: React.FC = () => {
  const { isAdmin, user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CallbackRequestItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const [clients, setClients] = useState<AppUser[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');

  const [events, setEvents] = useState<Event[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  const [selectedStatus, setSelectedStatus] = useState<string>('');

  const eventNameById = useMemo(() => {
    const map = new Map<string, string>();
    events.forEach(e => map.set(e._id, e.name));
    return map;
  }, [events]);

  useEffect(() => {
    if (isAdmin) fetchClients();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchEvents();
      setSelectedEventId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  useEffect(() => {
    fetchData(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedEventId, selectedStatus]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const resp = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      const users = (resp.data as any)?.data?.users || [];
      setClients(users);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load clients');
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoadingEvents(true);
      const params: any = { page: 1, limit: 100 };
      if (isAdmin && selectedClientId) params.clientId = selectedClientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const resp = await eventsApi.getAll(params);
      const list = (resp.data as any)?.data || [];
      setEvents(list);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const fetchData = async (page = 1, limit = 20) => {
    setLoading(true);
    try {
      const resp = await callbackRequestsApi.list({
        page,
        limit,
        status: selectedStatus || undefined,
        eventId: selectedEventId || undefined,
        clientId: isAdmin && selectedClientId ? selectedClientId : undefined,
      });
      const data: CallbackRequestItem[] = (resp.data as any)?.data || [];
      const pag = (resp.data as any)?.pagination || { page, limit, total: Array.isArray(data) ? data.length : 0 };
      setItems(data);
      setPagination({ page: pag.page, limit: pag.limit, total: pag.total });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (callbackRequestId: string, status: CallbackRequestItem['status']) => {
    try {
      await callbackRequestsApi.updateStatus(callbackRequestId, status);
      toast.success('Updated status');
      fetchData(pagination.page, pagination.limit);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to update status');
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">Callback Requests</h1>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <select
              value={selectedClientId}
              disabled={loadingClients}
              onChange={e => setSelectedClientId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[240px]"
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c._id} value={c._id}>{c.name} ({c.email})</option>
              ))}
            </select>
          )}
          <select
            value={selectedEventId}
            disabled={loadingEvents}
            onChange={e => setSelectedEventId(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[240px]"
          >
            <option value="">All Knowledge Assistants</option>
            {events.map(e => (
              <option key={e._id} value={e._id}>{e.name}</option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
          >
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={() => fetchData(1, pagination.limit)}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-[170px]">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-[160px]">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-[220px]">Knowledge Assistant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-[180px]">User ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide w-[160px]">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide min-w-[220px]">Issue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide min-w-[220px]">Expected Resolution</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">Loading...</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400 text-sm">No callback requests found</td>
                </tr>
              ) : (
                items.map(item => (
                  <tr key={item.callbackRequestId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {dayjs(item.createdAt).format('YYYY-MM-DD HH:mm')}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.status}
                        onChange={e => updateStatus(item.callbackRequestId, e.target.value as CallbackRequestItem['status'])}
                        className={`px-2 py-1 rounded-md text-xs font-medium border focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer ${
                          STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600 border-gray-200'
                        }`}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {eventNameById.get(item.eventId) || item.eventId}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                      {item.userId}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{item.phone}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-pre-wrap break-words">{item.issue ?? '-'}</td>
                    <td className="px-4 py-3 text-gray-700 whitespace-pre-wrap break-words">{item.expectedResolution ?? '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => fetchData(pagination.page - 1, pagination.limit)}
                disabled={pagination.page <= 1 || loading}
                className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 text-xs text-gray-600">Page {pagination.page} of {totalPages}</span>
              <button
                onClick={() => fetchData(pagination.page + 1, pagination.limit)}
                disabled={pagination.page >= totalPages || loading}
                className="p-1.5 rounded border border-gray-200 text-gray-500 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallbackRequests;
